/**
 * Regional configuration: currency, tax, dates and fiscal years.
 *
 * The DATA lives in regions.json, which is the single source of truth shared
 * with the backend (backend/region_config.py reads the very same file). This
 * module only provides accessors over it.
 *
 * To add a region or change a symbol, tax name or rate: edit regions.json.
 * Never hardcode a currency symbol or tax label anywhere else.
 */

import regionsData from './regions.json';

const REGION_DATA = regionsData.regions;
const ALIASES = regionsData._aliases || {};
const DEFAULT_REGION = regionsData._default || 'AU';

export const REGIONS = REGION_DATA;

/**
 * Resolve a possibly-aliased region code (e.g. 'GB' -> 'UK') to a canonical one.
 */
function resolveCode(regionCode) {
  if (!regionCode) return DEFAULT_REGION;
  const upper = String(regionCode).toUpperCase();
  const canonical = ALIASES[upper] || upper;
  return REGION_DATA[canonical] ? canonical : DEFAULT_REGION;
}

/**
 * Get region configuration by code. Always returns a valid region.
 */
export function getRegionConfig(regionCode = DEFAULT_REGION) {
  return REGION_DATA[resolveCode(regionCode)];
}

/**
 * Format a currency amount for a region.
 *
 * Uses the region's own locale so grouping and decimal separators are correct
 * (e.g. EUR renders as 1.234,56 rather than 1,234.56).
 */
export function formatCurrency(amount, regionCode = DEFAULT_REGION, { whole = false } = {}) {
  const region = getRegionConfig(regionCode);
  const value = whole ? Math.round(Number(amount) || 0) : Number(amount) || 0;

  // `whole` rounds to the unit and drops the cents, for headline figures
  const digits = whole ? 0 : 2;
  const formatted = Math.abs(value).toLocaleString(region.currency.locale, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });

  const withSymbol =
    region.currency.position === 'before'
      ? `${region.currency.symbol}${formatted}`
      : `${formatted}${region.currency.symbol}`;

  return value < 0 ? `-${withSymbol}` : withSymbol;
}

/**
 * A price in its own currency, whatever the viewer's region - for prices set
 * in one currency for everyone, such as subscription plans billed in USD.
 * `currency` is an ISO code ('usd', 'AUD'); the symbol and grouping come from
 * the region that uses it. Whole amounts drop the cents: "$7", "$1,500",
 * "$7.50".
 */
export function formatPrice(amount, currency) {
  const value = Number(amount) || 0;
  const code = String(currency || '').toUpperCase();
  const region = Object.values(REGION_DATA).find((r) => r.currency.code === code);
  const digits = Number.isInteger(value) ? 0 : 2;
  if (!region) {
    // A currency no region uses: let the browser name it ("CHF 7")
    return value.toLocaleString('en', { style: 'currency', currency: code || 'USD', minimumFractionDigits: digits, maximumFractionDigits: digits });
  }
  const formatted = Math.abs(value).toLocaleString(region.currency.locale, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
  const withSymbol = region.currency.position === 'before'
    ? `${region.currency.symbol}${formatted}`
    : `${formatted}${region.currency.symbol}`;
  return value < 0 ? `-${withSymbol}` : withSymbol;
}

/**
 * Currency symbol only (for use as a prefix/adornment on inputs).
 */
export function getCurrencySymbol(regionCode = DEFAULT_REGION) {
  return getRegionConfig(regionCode).currency.symbol;
}

/**
 * ISO currency code, e.g. 'AUD'.
 */
export function getCurrencyCode(regionCode = DEFAULT_REGION) {
  return getRegionConfig(regionCode).currency.code;
}

/**
 * Short tax name for the region, e.g. 'GST' / 'VAT' / 'Sales Tax'.
 */
export function getTaxName(regionCode = DEFAULT_REGION) {
  return getRegionConfig(regionCode).tax.name;
}

export function getTaxFullName(regionCode = DEFAULT_REGION) {
  return getRegionConfig(regionCode).tax.fullName;
}

export function getDefaultTaxRate(regionCode = DEFAULT_REGION) {
  return getRegionConfig(regionCode).tax.defaultRate;
}

/**
 * Business number label for the region, e.g. 'ABN' / 'EIN' / 'VAT Number'.
 */
export function getBusinessNumberLabel(regionCode = DEFAULT_REGION) {
  return getRegionConfig(regionCode).businessNumber.label;
}

/**
 * Example phone number for the region, for input placeholders.
 */
export function getPhonePlaceholder(regionCode = DEFAULT_REGION) {
  return getRegionConfig(regionCode).phone.placeholder;
}

/**
 * Whether the region has the full business-structure options and the payroll
 * obligations that come with them - superannuation, TFN, USI (AU only).
 * Mirrors has_business_structure in backend/region_config.py.
 */
export function hasBusinessStructure(regionCode = DEFAULT_REGION) {
  return Boolean(getRegionConfig(regionCode).hasBusinessStructure);
}

/**
 * Default super percentage charged on crew labour, or null where the region
 * has no such contribution (everywhere but AU). Mirrors get_crew_super_rate
 * in backend/region_config.py.
 */
export function getCrewSuperRate(regionCode = DEFAULT_REGION) {
  const crewSuper = getRegionConfig(regionCode).crewSuper;
  return crewSuper ? Number(crewSuper.defaultRate) : null;
}

export function hasCrewSuper(regionCode = DEFAULT_REGION) {
  return getCrewSuperRate(regionCode) !== null;
}

/**
 * All selectable regions, for dropdowns.
 */
export function getAvailableRegions() {
  return Object.values(REGION_DATA).map((region) => ({
    value: region.code,
    label: region.name,
  }));
}

// Timezones that pin a region exactly. Each maps to a preference list: the
// first entry that exists in regions.json wins, so a mapping may name a
// region before it ships (e.g. Dublin prefers IE, and falls back to EU until
// an IE region exists).
const TIMEZONE_REGIONS = {
  'Pacific/Auckland': ['NZ'],
  'Pacific/Chatham': ['NZ'],
  'Europe/London': ['UK'],
  'Europe/Belfast': ['UK'],
  'Europe/Guernsey': ['UK'],
  'Europe/Jersey': ['UK'],
  'Europe/Isle_of_Man': ['UK'],
  'Europe/Dublin': ['IE', 'EU'],
  'Africa/Johannesburg': ['ZA'],
  'Africa/Maseru': ['ZA'],
  'Africa/Mbabane': ['ZA'],
  'Asia/Singapore': ['SG'],
  'Pacific/Honolulu': ['US'],
};

// America/* is the US unless the city is Canadian.
const CANADA_TIMEZONES = new Set([
  'America/Toronto', 'America/Vancouver', 'America/Edmonton', 'America/Winnipeg',
  'America/Halifax', 'America/St_Johns', 'America/Regina', 'America/Moncton',
  'America/Whitehorse', 'America/Yellowknife', 'America/Iqaluit', 'America/Dawson',
  'America/Dawson_Creek', 'America/Fort_Nelson', 'America/Glace_Bay',
  'America/Goose_Bay', 'America/Swift_Current', 'America/Cambridge_Bay',
  'America/Inuvik', 'America/Rankin_Inlet', 'America/Resolute',
  'America/Atikokan', 'America/Blanc-Sablon', 'America/Creston',
]);

// Language subtags of euro countries, for when only navigator.language helps.
const EURO_LANGUAGE_REGIONS = new Set([
  'DE', 'FR', 'ES', 'IT', 'NL', 'BE', 'AT', 'PT', 'FI', 'GR',
  'SK', 'SI', 'LV', 'LT', 'EE', 'LU', 'MT', 'CY', 'HR',
]);

/**
 * Best guess at the visitor's region, from their timezone and browser
 * language. Returns a key of regions.json, or null when there is no basis
 * for a guess. It only ever pre-selects — the user confirms (the first-login
 * popup) or the caller falls back to the account's own region.
 *
 * Pass nothing in the app; the arguments exist so tests stay pure.
 */
export function detectRegion(timeZone, language) {
  let tz = timeZone;
  let lang = language;
  if (tz === undefined) {
    try {
      tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      tz = null;
    }
  }
  if (lang === undefined) {
    lang = typeof navigator !== 'undefined' ? navigator.language : null;
  }

  if (tz) {
    const candidates =
      TIMEZONE_REGIONS[tz] ||
      (tz.startsWith('Australia/') ? ['AU']
        : CANADA_TIMEZONES.has(tz) ? ['CA']
        : tz.startsWith('America/') ? ['US']
        : tz.startsWith('Europe/') ? ['EU']
        : []);
    for (const code of candidates) {
      if (REGION_DATA[code]) return code;
    }
  }

  if (lang && lang.includes('-')) {
    const subtag = lang.split('-').pop().toUpperCase();
    const canonical = ALIASES[subtag] || subtag;
    if (REGION_DATA[canonical]) return canonical;
    if (EURO_LANGUAGE_REGIONS.has(subtag) && REGION_DATA.EU) return 'EU';
  }

  return null;
}

/**
 * Format a date using the region's conventions.
 */
export function formatRegionalDate(dateString, regionCode = DEFAULT_REGION) {
  if (!dateString) return '';

  const region = getRegionConfig(regionCode);
  const date = new Date(dateString);

  if (isNaN(date.getTime())) return dateString;

  return date.toLocaleDateString(region.dateFormat.locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

/**
 * Date format pattern for the region (for input placeholders).
 */
export function getDateFormat(regionCode = DEFAULT_REGION) {
  return getRegionConfig(regionCode).dateFormat.short;
}

/**
 * The fiscal year start month (1-12) for a region.
 */
export function getFiscalYearStartMonth(regionCode = DEFAULT_REGION) {
  return getRegionConfig(regionCode).fiscalYearStartMonth || 1;
}

/**
 * Given a date and region, return fiscal year info.
 * Returns { startYear, endYear, key, label }
 * - key is a stable sortable identifier (the startYear)
 * - label is human-readable, e.g. "FY 2024–25" (or "FY 2024" for calendar-year regions)
 */
export function getFiscalYear(dateString, regionCode = DEFAULT_REGION) {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return null;

  const startMonth = getFiscalYearStartMonth(regionCode); // 1-12
  const month = date.getMonth() + 1; // 1-12
  const year = date.getFullYear();

  // If the month is before the fiscal start month, the FY started in the previous calendar year
  const startYear = month >= startMonth ? year : year - 1;
  const endYear = startYear + 1;

  const label =
    startMonth === 1
      ? `FY ${startYear}`
      : `FY ${startYear}–${String(endYear).slice(-2)}`;

  return { startYear, endYear, key: startYear, label };
}

/**
 * Given a date and region, return the fiscal quarter info.
 * Returns { number, label, monthsLabel }
 */
export function getFiscalQuarter(dateString, regionCode = DEFAULT_REGION) {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return null;

  const startMonth = getFiscalYearStartMonth(regionCode); // 1-12
  const month = date.getMonth() + 1; // 1-12

  // Months elapsed since fiscal start (0-11)
  const offset = (month - startMonth + 12) % 12;
  const quarter = Math.floor(offset / 3) + 1; // 1-4

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const qStartMonthIndex = ((startMonth - 1) + (quarter - 1) * 3) % 12;
  const qMonths = [0, 1, 2].map((i) => monthNames[(qStartMonthIndex + i) % 12]);

  return {
    number: quarter,
    label: `Q${quarter}`,
    monthsLabel: `${qMonths[0]}–${qMonths[2]}`,
  };
}
